import axios from 'axios'
import { execute_generator, download_file } from './index'

export const api_url = process.env.VUE_APP_API_URL

// FI-1871: persist the current user's site theme (original | new) under their
// preferences map. authed via the jwt; the backend coerces unknown values.
export const update_site_theme = (theme) => execute_generator(
    'PUT', { service: api_url, resource: 'bookings/auth/site-theme' }, { site_theme: theme }
).next()

// FI-2180: trade the current (still valid) jwt for a fresh one. the api reads
// the token off the Authorization header and ignores the body; the reply is
// the login envelope ({token, user}) so the caller writes it back the same
// way login does (utils/session_renewal applyRenewedSession). noAuthRedirect:
// a rejection here is the caller's to classify, never a blind bounce to login.
export const refresh_session = () => execute_generator(
    'POST', { service: api_url, resource: 'bookings/auth/refresh' }, {}, { noAuthRedirect: true }
).next()

// classic sunset: records that the one-time "welcome to horizon" sheet was
// seen (preferences.classic_sunset_notice pending -> dismissed). server side
// so it shows once per account, not once per browser: login clears
// localStorage. the api also flips it when the theme changes via the picker.
export const dismiss_classic_sunset_notice = () => execute_generator(
    'PUT', { service: api_url, resource: 'bookings/auth/classic-sunset-notice' }, { dismissed: true }
).next()

// main dashboard widget layout (show/hide + order), stored under the same
// per-user preferences map as site-theme.
export const get_dashboard_config = () => execute_generator(
    'GET', { service: api_url, resource: 'bookings/auth/dashboard-config' }
).next()

export const update_dashboard_config = (widgets) => execute_generator(
    'PUT', { service: api_url, resource: 'bookings/auth/dashboard-config' }, { widgets }
).next()

// the dashboard's stats window (1/3/7/30/90 days), remembered per user under
// the same preferences map. separate from the layout PUT above because the
// range changes on a click in view mode, not at the end of a Customize
// session; it reads back on the dashboard-config GET.
export const update_dashboard_range = (days_range) => execute_generator(
    'PUT', { service: api_url, resource: 'bookings/auth/dashboard-range' }, { days_range }
).next()

// starred nav destinations (the sidebar Favorites section), stored under the
// same per-user preferences map. server-side rather than localStorage because
// login.vue clears localStorage on every visit to the login page.
export const get_nav_favorites = (config) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/auth/nav-favorites' }, undefined, config
).next()

export const update_nav_favorites = (favorites) => execute_generator(
    'PUT', { service: api_url, resource: 'bookings/auth/nav-favorites' }, { favorites }
).next()

// AWS-console-style favorites bar under the horizon top nav, on/off. stored
// under the same per-user preferences map; the value flows back via login's
// data.user, same as site-theme, so there's no dedicated GET.
export const update_nav_favorites_bar = (enabled) => execute_generator(
    'PUT', { service: api_url, resource: 'bookings/auth/nav-favorites-bar' }, { enabled }
).next()

// the group message sender's remembered filter set (user type, format, job titles, bases), kept
// per tenant under the same per-user preferences map. server-side for the same reason favorites
// are: login clears localStorage, so a browser-only copy would reset at every login.
export const get_group_message_filters = () => execute_generator(
    'GET', { service: api_url, resource: 'bookings/auth/group-message-filters' }
).next()

export const update_group_message_filters = (tenant, filters) => execute_generator(
    'PUT', { service: api_url, resource: 'bookings/auth/group-message-filters' }, { tenant, filters }
).next()

// the ask box's typeahead history. server-side for the same reason favorites
// are: login clears localStorage, and the list is meant to follow the user
// across devices and browsers rather than pile up per-machine. entries are
// { q, ts } so two devices' partial lists can be merged by recency.
export const get_recent_questions = () => execute_generator(
    'GET', { service: api_url, resource: 'bookings/auth/recent-questions' }
).next()

export const update_recent_questions = (questions) => execute_generator(
    'PUT', { service: api_url, resource: 'bookings/auth/recent-questions' }, { questions }
).next()

// tenant-scoped object search behind the top bar's ask box typeahead
// (drivers, vehicles, bookings, users, ops events)
export const search_objects = (tenant, q) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/search?tenant=' + tenant + '&q=' + encodeURIComponent(q || '') }
).next()

export const get_actuals = (tenant, filter, booking_search, selected_company, selected_type, fromDate, toDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/actuals/json?tenant=' + tenant + '&filter=' + filter + '&booking_search=' + encodeURIComponent(booking_search || '') + '&company=' + selected_company + '&type=' + selected_type + '&fromDate=' + fromDate + '&toDate=' + toDate}
).next()

export const get_fasttrak_actuals = (tenant, filter, booking_search, selected_company, selected_type, fromDate, toDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/actuals/fasttrak/json?tenant=' + tenant + '&filter=' + filter + '&booking_search=' + encodeURIComponent(booking_search || '') + '&company=' + selected_company + '&type=' + selected_type + '&fromDate=' + fromDate + '&toDate=' + toDate}
).next()

export const get_moovs_actuals = (tenant, filter, booking_search, selected_company, selected_type, fromDate, toDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/actuals/moovs/json?tenant=' + tenant + '&filter=' + filter + '&booking_search=' + encodeURIComponent(booking_search || '') + '&company=' + selected_company + '&type=' + selected_type + '&fromDate=' + fromDate + '&toDate=' + toDate}
).next()

export const get_moovs_booking_history = (tenant, processed_time) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/actuals/moovs/history?tenant=' + tenant + '&processed_time=' + encodeURIComponent(processed_time)}
).next()

export const reprocess_actuals = (tenant, filter, selected_company, selected_type, selected_status, fromDate, toDate) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/actuals/reprocess?tenant=' + tenant + '&filter=' + filter + '&status=' + selected_status + '&company=' + selected_company + '&type=' + selected_type + '&fromDate=' + fromDate + '&toDate=' + toDate}
).next()

export const get_actuals_error_summary = (tenant, fromDate, toDate, filter, company, type) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/actuals/error_summary?tenant=' + tenant + '&start_date=' + fromDate + '&end_date=' + toDate + '&filter=' + (filter || 'finish') + '&company=' + (company || '') + '&type=' + (type || '')}
).next()

export const get_actuals_planned_vs_actual_summary = (tenant, fromDate, toDate, filter, company, type) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/actuals/planned_vs_actual?tenant=' + tenant + '&start_date=' + fromDate + '&end_date=' + toDate + '&filter=' + (filter || 'finish') + '&company=' + (company || '') + '&type=' + (type || '')}
).next()

export const get_actuals_on_time_report = (tenant, fromDate, toDate, filter, company, type, toYardMinutes, departYardMinutes, toPickupMinutes) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/actuals/on_time?tenant=' + tenant + '&start_date=' + fromDate + '&end_date=' + toDate + '&filter=' + (filter || 'finish') + '&company=' + (company || '') + '&type=' + (type || '')
        + '&to_yard_minutes=' + toYardMinutes + '&depart_yard_minutes=' + departYardMinutes + '&to_pickup_minutes=' + toPickupMinutes}
).next()

export const get_actuals_hos_timeline = (tenant, bookingId, driverName) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/actuals/hos_timeline?tenant=' + tenant + '&booking_id=' + encodeURIComponent(bookingId || '') + (driverName ? '&driver_name=' + encodeURIComponent(driverName) : '')}
).next()

export const get_actuals_processing_rules = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/actuals/processing_rules?tenant=' + tenant}
).next()

export const save_actuals_feedback = (tenant, body) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/actuals/feedback?tenant=' + tenant }, body
).next()

export const clear_actuals_feedback_for_date_range = (tenant, fromDate, toDate) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/actuals/feedback?tenant=' + tenant + '&fromDate=' + fromDate + '&toDate=' + toDate }
).next()

export const get_booking_dashboard = (tenant, selected_base, expireHrs, nextHrs) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/booking/dashboard?tenant=' + tenant + "&selected_base=" + selected_base + "&expire_hours=" + expireHrs + "&next_hours=" + nextHrs}
).next()

export const get_booking_feed_history = (tenant, filter, booking_search, fromDate, toDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/feed/history?tenant=' + tenant + '&filter=' + filter + '&booking_search=' + encodeURIComponent(booking_search || '') + '&fromDate=' + fromDate + '&toDate=' + toDate}
).next()

export const get_actuals_payroll_summary_report = (tenant, driver, from_date, to_date, reprocess, config) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/actuals/payroll/summary?tenant=' + tenant + '&driver=' + driver  + '&fromDate=' + from_date + '&toDate=' + to_date + '&reprocess=' + reprocess + (config && config.poll ? '&poll=true' : '')}, undefined, config
).next()

export const get_trip_sheet = (tenant, date) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/tripsheet?tenant=' + tenant + "&date=" + date}
).next()

export const get_driver_board = (tenant, base) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/driverboard?tenant=' + tenant + "&base=" + base}
).next()

export const get_vehicle_maint_summary = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehmaintsummary?tenant=' + tenant}
).next()

export const get_dvir_defects = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicles/defects?tenant=' + tenant}
).next()

export const get_report_history = (tenant, report_type, date) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/history?tenant=' + tenant + '&reportType=' + report_type + '&date=' + date}
).next()

export const request_report = (tenant, report_type, date) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/request?tenant=' + tenant + '&reportType=' + report_type + '&date=' + date}
).next()

export const get_actuals_performance_report = (tenant, date) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/actuals/performance?tenant=' + tenant + '&date=' + date}
).next()

export const get_actuals_multiday_otp_report = (tenant, base, client, fromDate, toDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/actuals/multiday/otp?tenant=' + tenant + '&base=' + base + '&client=' + client + '&fromDate=' + fromDate + '&toDate=' + toDate }
).next()

export const get_actuals_mileage_report = (tenant, date, reprocess) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/actuals/mileage?tenant=' + tenant + '&date=' + date + '&reprocess=' + reprocess}
).next()

export const get_actuals_billing_report = (tenant, date) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/actuals/billing?tenant=' + tenant + '&date=' + date}
).next()

export const get_actuals_vehicle_report = (tenant, booking_type, date) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/actuals/vehicle?tenant=' + tenant + '&booking_type=' + booking_type + '&date=' + date}
).next()

export const get_smart_route_assignment_vehicle_types = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/smart_route_assignments/vehicle/types?tenant=' + tenant}
).next()

export const get_smart_route_assignments = (tenant, vehicleTypes, fromDate, toDate, autoselect) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/smart_route_assignments?tenant=' + tenant + '&vehicleTypes=' + vehicleTypes + '&fromDate=' + fromDate + '&toDate=' + toDate + '&autoselect=' + autoselect}
).next()

export const update_smart_route_assignment = (tenant, body) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/smart_route_assignments?tenant=' + tenant}, body
).next()

export const get_cm_vehicle_map_future_data = (
  tenant, from_date, to_date, vehicle = '', driver = '', search = ''
) => execute_generator(
  'GET',
  {
    service: api_url,
    resource:
      'bookings/vehicle_map_future_data?tenant=' + tenant +
      '&fromDate=' + from_date +
      '&toDate='   + to_date +
      (vehicle ? '&vehicle=' + encodeURIComponent(vehicle) : '') +
      (driver  ? '&driver='  + encodeURIComponent(driver)  : '')
  }
).next()

export const get_cm_overlap_updates_history = (tenant, startDate, endDate) => execute_generator(
  'GET',
  {
    service: api_url,
    resource: 'bookings/actuals/overlap_updates/history?tenant=' + tenant + '&startDate=' + startDate + '&endDate=' + endDate
  }
).next()

export const get_otp_report = (tenant, date, routeFilter) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/otp?tenant=' + tenant + '&date=' + date + '&route_filter=' + routeFilter}
).next()

export const get_otp_history = (tenant, fromDate, toDate, routeFilter, tripFilter, stopFilter, vehicleFilter, driverFilter) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/otp/history?tenant=' + tenant + '&fromDate=' + fromDate + '&toDate=' + toDate + '&route_filter=' + routeFilter + '&trip_filter=' + tripFilter + '&stop_filter=' + stopFilter + '&vehicle_filter=' + vehicleFilter + '&driver_filter=' + driverFilter}
).next()

export const get_actuals_driver_payroll_report = (tenant, tag, date, reprocess, config) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/actuals/driverpayroll?tenant=' + tenant + '&tag=' + tag + '&date=' + date + '&reprocess=' + reprocess + (config && config.poll ? '&poll=true' : '')}, undefined, config
).next()

export const get_hos_by_client_report = (tenant, from, to, reprocess) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/actuals/hoursByClient?tenant=' + tenant + '&fromDate=' + from + '&toDate=' + to + '&reprocess=' + reprocess}
).next()

export const get_actuals_drivers_daily_report = (tenant, date, reprocess) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/actuals/driver?tenant=' + tenant + '&date=' + date + '&reprocess=' + reprocess}
).next()

export const get_actuals_driver_multiday_report = (tenant, tag, driver, from_date, to_date, reprocess, config) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/actuals/driver/multiday?tenant=' + tenant + '&tag=' + tag + '&driver=' + driver + '&fromDate=' + from_date + '&toDate=' + to_date + '&reprocess=' + reprocess + (config && config.poll ? '&poll=true' : '')}, undefined, config
).next()

export const get_actuals_mileage_by_booking_report = (tenant, from_date, to_date) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/actuals/mileage_by_booking?tenant=' + tenant + '&fromDate=' + from_date + '&toDate=' + to_date}
).next()

export const push_actuals_driver_multiday_to_payroll = (tenant, tag, driver, from_date, to_date) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/actuals/driver/multiday/push?tenant=' + tenant + '&tag=' + tag + '&driver=' + driver + '&fromDate=' + from_date + '&toDate=' + to_date}
).next()

export const get_vehicle_utilization = (tenant, date) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicle/utilization?tenant=' + tenant + '&date=' + date}
).next()

export const get_driver_utilization = (tenant, date) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/driver/utilization?tenant=' + tenant + '&date=' + date}
).next()

export const get_vehicle_calendar = (tenant, hours_offset, date, base, tag) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicle/calendar?tenant=' + tenant + '&hours_offset=' + hours_offset + '&date=' + date + '&base=' + base + '&tag=' + tag}
).next()

export const get_vehicle_type_calendar = (tenant, hours_offset, fromDate, toDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicle/type/calendar?tenant=' + tenant + '&hours_offset=' + hours_offset + '&fromDate=' + fromDate + '&toDate=' + toDate}
).next()

export const get_driver_type_calendar = (tenant, type_mode, hours_offset, fromDate, toDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/driver/type/calendar?tenant=' + tenant + '&type_mode=' + (type_mode || '') + '&hours_offset=' + (hours_offset || '') + '&fromDate=' + fromDate + '&toDate=' + toDate}
).next()

export const get_vehicle_map = (tenant, vehicle_id, samsara_vehicle_id, booking_id) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicle/map?tenant=' + tenant + "&vehicleId=" + vehicle_id + "&samsaraVehicleId=" + samsara_vehicle_id + "&bookingId=" + booking_id}
).next()

export const get_vehicle_map_direct = (tenant, vehicle_id, samsara_vehicle_id, booking_id) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicle/map/direct?tenant=' + tenant + "&vehicleId=" + vehicle_id + "&samsaraVehicleId=" + samsara_vehicle_id + "&bookingId=" + booking_id}
).next()

export const get_vehicle_map_driver = (tenant, vehicle_id, samsara_vehicle_id, booking_id) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicle/map/driver?tenant=' + tenant + "&vehicleId=" + vehicle_id + "&samsaraVehicleId=" + samsara_vehicle_id + "&bookingId=" + booking_id}
).next()

export const get_vehicle_track = (tenant, mode, value, interval) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicle/track?tenant=' + tenant + "&mode=" + mode + "&value=" + value + "&interval=" + interval}
).next()

export const get_vehicle_trip_history = (tenant, value, fromDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicle/trip/history?tenant=' + tenant + "&vehicle=" + value + "&fromDate=" + fromDate}
).next()

export const get_vehicle_route_history = (tenant, route_name, vehicle_id, fromDate, minutesBuffer) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicle/route/history?tenant=' + tenant + "&route_name=" + route_name + "&vehicle=" + vehicle_id + "&fromDate=" + fromDate + "&minutesBuffer=" + minutesBuffer}
).next()

export const get_vehicle_route_locations = (tenant, routeID, minutesBuffer) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicle/route/locations?tenant=' + tenant + "&routeID=" + routeID + "&minutesBuffer=" + minutesBuffer}
).next()

export const get_vehicle_list = (tenant, fromDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicles/list?tenant=' + tenant + "&fromDate=" + fromDate}
).next()

export const get_route_list = (tenant, fromDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/routes/list?tenant=' + tenant + "&fromDate=" + fromDate}
).next()

export const get_route_names = (tenant, fromDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/routes/names?tenant=' + tenant + "&fromDate=" + fromDate}
).next()

export const get_route_patterns = (tenant, fromDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/routes/patterns?tenant=' + tenant + "&fromDate=" + fromDate}
).next()

export const get_manage_drivers_list = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/drivers/manage/list?tenant=' + tenant}
).next()

export const get_driver_usernames_list = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/drivers/usernames/list?tenant=' + tenant}
).next()

export const get_tag_list = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/tags/list?tenant=' + tenant}
).next()

export const get_yard_list = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/yards/list?tenant=' + tenant}
).next()

export const get_vehicle_track_fleet = (tenant, mode, value) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicle/track/fleet?tenant=' + tenant + "&mode=" + mode + "&value=" + value}
).next()

export const get_vehicle_track_reload = (tenant, mode, value) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicle/track/reload?tenant=' + tenant + "&mode=" + mode + "&value=" + value}
).next()

export const get_bus_tracker_results = (tenant, departureDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicle/bustracker?tenant=' + tenant + "&departureDate=" + departureDate}
).next()

export const get_bus_tracker_status = (tenant, departureDate, routeName, departureTime, departingFrom, arrivingAt) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicle/bustracker/status?tenant=' + tenant + "&departureDate=" + departureDate + "&routeName=" + routeName + "&departureTime=" + departureTime + "&departingFrom=" + departingFrom + "&arrivingAt=" + arrivingAt}
).next()

export const get_admin_dashboard = (tenant, date, tenant_filter,) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/admin/dashboard?tenant=' + tenant + '&date=' + date + '&tenant_filter=' + tenant_filter}
).next()

export const get_tenant_dashboard = (tenant, date, days = 30) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/tenant/dashboard?tenant=' + tenant + '&date=' + date + '&days=' + days}
).next()

// "At a Glance" widget. days is the dashboard's selected range: every figure
// covers the last that-many days ending today, resolved server-side in the
// tenant's own timezone ("out now" excepted -- that one is always real-time).
export const get_tenant_glance = (tenant, days = 7) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/tenant/glance?tenant=' + tenant + '&days=' + days}
).next()

export const get_all_tenants_config = (demoOnly = false) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/tenant/config/all' + (demoOnly ? '?demo_only=true' : '')}
).next()

export const get_tenant_billing_enrichment = (demoOnly = false) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/tenant/config/all/billing' + (demoOnly ? '?demo_only=true' : '')}
).next()

export const get_tenant_config_history = (tenant, startDate, endDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/tenant/config/history?tenant=' + tenant + '&startDate=' + startDate + '&endDate=' + endDate}
).next()

export const get_all_tenant_config_history = (sinceDate, untilDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/admin/config/history?sinceDate=' + encodeURIComponent(sinceDate) + (untilDate ? '&untilDate=' + encodeURIComponent(untilDate) : '')}
).next()

export const register_tenant = (config) => execute_generator(
    'POST',{ service: api_url, resource: 'bookings/tenant/register'}, config).next()

// bus sos yard-only registration request (public, rate-limited). persists
// nothing: the api emails the request to support, who create the tenant by
// hand. the api's 400s carry a human message the page shows as-is (see
// error_message_from in sos_register.vue).
export const register_sos_yard_only = (body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/sos/register' }, body).next()

export const update_tenant_config_settings = (tenant, config) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/tenant/config/settings?tenant=' + tenant}, config).next()

export const get_tenant_config_settings = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/tenant/config/settings?tenant=' + tenant}
).next()

// one tenant's live Stripe subscription, for the Packages settings tab. Readable
// by that tenant's own customer-admins, so the server projects it down to package
// lines and amounts -- the customer name/email and subscription id ride along only
// for staff. Not to be confused with get_tenant_billing_enrichment above, which is
// the fleet-wide admin version.
export const get_tenant_package_billing = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/tenant/config/billing?tenant=' + tenant}
).next()
    
// mode='widgets' returns only the compact per-record fields the dashboard
// widgets aggregate (no message/email bodies), with a higher server-side
// results cap; omit it for the full alert history records.
export const get_alerts = (tenant, alertType, startDate, endDate, mode) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/alerts?tenant=' + tenant + '&alertType=' + alertType + '&startDate=' + startDate + '&endDate=' + endDate + (mode ? '&mode=' + mode : '')}
).next()

export const delete_alert = (tenant, movementVehicleId) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/alerts?tenant=' + tenant + '&movementVehicleId=' + encodeURIComponent(movementVehicleId)}
).next()

export const save_alert_feedback = (tenant, body) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/alerts/feedback?tenant=' + tenant }, body
).next()

export const clear_alert_feedback_for_date_range = (tenant, fromDate, toDate) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/alerts/feedback?tenant=' + tenant + '&fromDate=' + fromDate + '&toDate=' + toDate }
).next()

export const get_hos_history = (tenant, driver, eventType, remarks_filter, startDate, endDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/hos/history?tenant=' + tenant + '&driver=' + driver + '&eventType=' + eventType + '&remarksFilter=' + remarks_filter + '&startDate=' + startDate + '&endDate=' + endDate}
).next()

export const get_booking_file_history = (tenant, startDate, endDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/file/history?tenant=' + tenant + '&startDate=' + startDate + '&endDate=' + endDate}
).next()

export const send_event_notes_payroll_category_reports = (tenant, startDate, endDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/event/notes/payrollcategoryreports?tenant=' + tenant + '&startDate=' + startDate
            + '&endDate=' + endDate}
).next()

export const get_event_notes = (tenant, startDate, endDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/event/notes?tenant=' + tenant + '&startDate=' + startDate
            + '&endDate=' + endDate}
).next()

// view tags the request with the board that sent it (the api ignores it, but
// the request log keeps it, so board usage shows up in Datadog)
export const get_vehicle_info = (tenant, base, tag, return_mode, driver_view, view) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicles?tenant=' + tenant + '&base=' + base + '&tag=' + tag + '&returnMode=' + return_mode + '&driverView=' + !!driver_view + (view ? '&view=' + view : '')}
).next()

export const get_vehicle_info_fasttrak = (tenant, base, tag, return_mode) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicles/fasttrak?tenant=' + tenant + '&base=' + base + '&tag=' + tag + '&returnMode=' + return_mode}
).next()

export const get_vehicle_info_moovs = (tenant, base, tag, return_mode) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicles/moovs?tenant=' + tenant + '&base=' + base + '&tag=' + tag + '&returnMode=' + return_mode}
).next()

export const get_vehicle_info_history = (tenant, vehicleName) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vehicles/history?tenant=' + tenant + '&vehicleName=' + vehicleName}
).next()

export const get_vehicle_sync_history = (tenant, vehicleName, filter, sourceFilter, startDate, endDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/vehiclestatus/sync-history?tenant=' + tenant + '&vehicleName=' + vehicleName + '&filter=' + filter + '&sourceFilter=' + sourceFilter + '&startDate=' + startDate + '&endDate=' + endDate}
).next()

export const get_vehicle_status_history = (tenant, tagName, vehicleName, filter, statusFilter, startDate, endDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/reports/vehiclestatus/history?tenant=' + tenant + '&tagName=' + tagName + '&vehicleName=' + vehicleName + '&filter=' + filter + '&statusFilter=' + statusFilter +'&startDate=' + startDate
            + '&endDate=' + endDate}
).next()

// eventName is free-text tenant config and really does contain spaces in prod
// ("24hr Notification"), so it has to be encoded or a name with & or + silently
// resolves to the wrong event (or none) on the API side.
export const reprocess_alerts_for_booking = (tenant, alert_type, booking_hash, event_name ) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/alerts/reprocess?tenant=' + tenant + '&alertType=' + alert_type + '&bookingID=' + booking_hash + (event_name ? '&eventName=' + encodeURIComponent(event_name) : '') }
).next()

export const test_html_alert = (tenant, alert_type ) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/alerts/test/email/template?tenant=' + tenant + '&alertType=' + alert_type }
).next()

export const test_html_alert_param = (tenant, alert_type, event_name ) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/alerts/test/email/template/event?tenant=' + tenant + '&alertType=' + alert_type + '&eventName=' + event_name}
).next()

export const save_booking_notes = (tenant, body) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/notes?tenant=' + tenant}, body
).next()

export const save_event_notes = (tenant, body) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/event/notes?tenant=' + tenant}, body
).next()

export const get_vsm_upcoming = (tenant, base, fromDate, toDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/vsm/upcoming?tenant=' + tenant + '&base=' + (base || '') + '&fromDate=' + fromDate + '&toDate=' + toDate}
).next()

export const update_vehicle_status = (tenant, body) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/vehicles?tenant=' + tenant}, body
).next()

export const delete_vehicle_status = (tenant, body) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/vehicles?tenant=' + tenant}, body
).next()

export const update_vehicle_status_anonymous = (tenant, body) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/vehicles/anonymous?tenant=' + tenant}, body
).next()

// direction: 'sent' (this company raised), 'received' (from another company), or omitted (both)
export const get_sos_history = (tenant, startDate, endDate, direction) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/history?tenant=' + tenant + '&startDate=' + startDate
            + '&endDate=' + endDate + (direction ? '&direction=' + direction : '')}
).next()

// authenticated dashboard detail: full event (messages, recipients, responses), scoped to
// an owner tenant server-side. the payload carries event.share_token, which authorizes every
// token-based action the page then takes.
export const get_sos_details_authed = (tenant, request_id) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/' + request_id + '/detail?tenant=' + tenant}
).next()

// admin tools fleet map: every active tenant's current vehicle positions (staff only)
export const get_admin_fleet_map = () => execute_generator(
    'GET', { service: api_url, resource: 'bookings/admin/fleet_map' }
).next()

export const get_sos_overlay = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/overlay?tenant=' + tenant}
).next()

export const send_sos_request = (tenant, vehicle_name, request_notes) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/sos/send?vehicle_name=' + vehicle_name + '&request_notes=' + request_notes + '&tenant=' + tenant}
).next()

export const send_sos_pin_request = (tenant, lat, lon, opts = {}) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/sos/send/pin?lat=' + lat + '&lon=' + lon
            + '&request_notes=' + encodeURIComponent(opts.request_notes || '')
            + '&label=' + encodeURIComponent(opts.label || '')
            + '&vehicle_name=' + encodeURIComponent(opts.vehicle_name || '')
            + '&vehicle_type=' + encodeURIComponent(opts.vehicle_type || '')
            + '&driver_name=' + encodeURIComponent(opts.driver_name || '')
            + '&driver_phone=' + encodeURIComponent(opts.driver_phone || '')
            + '&operator_contact_name=' + encodeURIComponent(opts.operator_contact_name || '')
            + '&operator_contact_phone=' + encodeURIComponent(opts.operator_contact_phone || '')
            + '&operator_contact_email=' + encodeURIComponent(opts.operator_contact_email || '')
            + '&broadcast=' + (opts.broadcast ? 'true' : 'false')
            + '&tenant=' + tenant}
).next()

// admin: set the human-readable slug behind the public driver SOS form URL
export const set_sos_form_slug = (tenant, slug) => execute_generator(
    'PUT', { service: api_url, resource: 'bookings/sos/slug?tenant=' + tenant}, { slug }
).next()

// public (unauth) driver SOS form — slug in the path resolves the tenant server-side;
// backend ignores the Bearer null header and returns 404 (not 403) for a bad slug so the
// visitor is not bounced to login
export const get_sos_form = (slug) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/form/' + encodeURIComponent(slug)}
).next()

export const submit_sos_form = (slug, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/sos/form/' + encodeURIComponent(slug) + '/submit'}, body
).next()

// public pre-submit lookup: validate a Vehicle ID and get its current location for the map
export const locate_sos_vehicle = (slug, vehicle) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/form/' + encodeURIComponent(slug) + '/locate?vehicle=' + encodeURIComponent(vehicle)}
).next()

// public reverse lookup: given a dropped pin, identify the vehicle + driver if exactly one is there
export const identify_sos_at_location = (slug, lat, lon) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/form/' + encodeURIComponent(slug) + '/identify?lat=' + lat + '&lon=' + lon}
).next()

// public (unauth) info-only directory lookup - returns { yards } near a point
export const get_sos_nearby = (lat, lon, radius) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/nearby?lat=' + lat + '&lon=' + lon + '&radius=' + radius}
).next()

// public (unauth) full directory - returns { yards } for every yard (the yard map's default view)
export const get_sos_all_yards = () => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/yards'}
).next()

// snapshot-backed preview of the potential broadcast audience for the Send SOS page: named opted-in
// partner yards in range + an anonymous count of partners with a vehicle in range (positions as of
// the last ~2-hourly fleet snapshot). Fast - no live per-partner telematics fan-out.
export const get_sos_broadcast_preview = (tenant, lat, lon, radius) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/broadcast-preview?tenant=' + tenant + '&lat=' + lat + '&lon=' + lon + '&radius=' + radius}
).next()

// public geocoding for the driver form: address -> { found, lat, lon, address }; pin -> { address }
export const geocode_sos_form_address = (slug, address) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/form/' + encodeURIComponent(slug) + '/geocode?address=' + encodeURIComponent(address)}
).next()
export const revgeocode_sos_form_pin = (slug, lat, lon) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/form/' + encodeURIComponent(slug) + '/revgeocode?lat=' + lat + '&lon=' + lon}
).next()

// authed dispatcher Send SOS: locate a Vehicle ID's current position to drop/update the pin
// (returns { found, vehicle_name, lat, lon, address, driver })
export const locate_sos_vehicle_authed = (tenant, vehicle) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/locate?tenant=' + tenant + '&vehicle=' + encodeURIComponent(vehicle)}
).next()

// authed geocoding for the dispatcher Send SOS page (same shapes)
export const geocode_sos_address = (tenant, address) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/geocode?tenant=' + tenant + '&address=' + encodeURIComponent(address)}
).next()
export const revgeocode_sos_pin = (tenant, lat, lon) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/revgeocode?tenant=' + tenant + '&lat=' + lat + '&lon=' + lon}
).next()

// public (unauth) driver status view resolved by the per-event share token (/sos/<slug>/<token>)
export const get_sos_driver_status = (token) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/token/' + encodeURIComponent(token)}
).next()

// public, token-addressed rich detail — same payload shape as the authed read, but with the
// event public-stripped. carries event.role ('sender' | 'receiver') so a thin wrapper can route
// to the right page.
export const get_sos_detail_by_token = (token) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/token/' + encodeURIComponent(token) + '/detail'}
).next()

// live "can this bus help?" context for one of an SOS's own nearby vehicles, fetched on demand when
// the dispatcher expands it: fresh speed/heading, a last-hour GPS breadcrumb, the active booking + a
// passenger-likelihood estimate, and (authed only) the assigned driver + HOS. vehicle is the
// telematics id or the vehicle name.
export const get_sos_vehicle_context = (tenant, request_id, vehicle) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/' + encodeURIComponent(request_id) + '/vehicle-context?tenant=' + tenant + '&vehicle_id=' + encodeURIComponent(vehicle)}
).next()

// public, token-addressed variant (driver + HOS withheld); same movement context otherwise
export const get_sos_vehicle_context_by_token = (token, vehicle) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/token/' + encodeURIComponent(token) + '/vehicle-context?vehicle_id=' + encodeURIComponent(vehicle)}
).next()

// record a responder's acknowledgement / status on an SOS (closes the loop for the source)
export const respond_to_sos_by_token = (token, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/sos/token/' + encodeURIComponent(token) + '/respond'}, body || {}
).next()

// escalate a company-internal SOS to a broadcast to nearby opted-in partner companies; updates
// optionally carries reviewed/edited request fields to apply before broadcasting
export const broadcast_sos_by_token = (token, updates) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/sos/token/' + encodeURIComponent(token) + '/broadcast'}, updates || {}
).next()

// free-text status update to the same group a prior broadcast reached ("found a bus, stand down")
export const rebroadcast_sos_by_token = (token, message) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/sos/token/' + encodeURIComponent(token) + '/rebroadcast'}, { message }
).next()

// change the SOS pin location / radius (body: { lat, lon, max_miles_away })
export const update_sos_by_token = (token, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/sos/token/' + encodeURIComponent(token) + '/update'}, body || {}
).next()

export const close_sos_by_token = (token) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/sos/token/' + encodeURIComponent(token) + '/close'}, {}
).next()

export const cancel_sos_by_token = (token) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/sos/token/' + encodeURIComponent(token) + '/cancel'}, {}
).next()

// post a comment to the SOS thread. body: { text, author, role: 'driver' | 'dispatcher' }
export const add_sos_comment_by_token = (token, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/sos/token/' + encodeURIComponent(token) + '/comment'}, body || {}
).next()

// toggle whether the source booking itinerary is shared with the partner (broadcast) receivers.
// One method serves both the signed-in sender view and the standalone dispatcher page - both write
// via the event's share token (actionToken).
export const set_sos_itinerary_shared_by_token = (token, shared) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/sos/token/' + encodeURIComponent(token) + '/share-itinerary'}, { shared }
).next()

// delete an SOS entirely (token-addressed)
export const delete_sos_by_token = (token) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/sos/token/' + encodeURIComponent(token)}
).next()

// tenant bus_sos flags for any signed-in user (enabled, send/receive_to_other, slug)
export const get_sos_settings = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/sos/settings?tenant=' + tenant}
).next()

export const delete_booking = (tenant, booking_id) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/' + booking_id + '?tenant=' + tenant}
).next()

export const delete_tenant = (tenant) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/tenant/' + tenant}
).next()

export const delete_yard = (tenant, yard_name) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/tenant/yard?yard_name=' + yard_name + '&tenant=' + tenant}
).next()

export const add_yard = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/tenant/yard?tenant=' + tenant}, body
).next()

export const update_yard = (tenant, body) => execute_generator(
    'PUT', { service: api_url, resource: 'bookings/tenant/yard?tenant=' + tenant}, body
).next()

export const add_hos_event = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/tenant/hos/event?tenant=' + tenant}, body
).next()

export const delete_hos_event = (tenant, event_name) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/tenant/hos/event?event_name=' + event_name + '&tenant=' + tenant}
).next()

export const add_vehicle_late_alert_event = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/tenant/vehiclelatealert/event?tenant=' + tenant}, body
).next()

export const delete_vehicle_late_alert_event = (tenant, event_name) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/tenant/vehiclelatealert/event?event_name=' + event_name + '&tenant=' + tenant}
).next()

export const add_booking_state_alert_event = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/tenant/bookingstatealert/event?tenant=' + tenant}, body
).next()

export const delete_booking_state_alert_event = (tenant, event_name) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/tenant/bookingstatealert/event?event_name=' + event_name + '&tenant=' + tenant}
).next()

export const add_cust_event = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/tenant/cust/event?tenant=' + tenant}, body
).next()

export const delete_cust_event = (tenant, event_name) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/tenant/cust/event?event_name=' + event_name + '&tenant=' + tenant}
).next()

export const get_users = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/users/list?tenant=' + tenant}
).next()

export const get_primary_users = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/users/primary/list?tenant=' + tenant}
).next()

export const get_contact_users = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/users/contacts/list?tenant=' + tenant}
).next()

export const get_driver_list = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/drivers/list?tenant=' + tenant}
).next()

export const get_support_tickets = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/support_tickets/list?tenant=' + tenant}
).next()

export const search_support_tickets = (tenant, assigned, category, priority, subject, ticket_status, startDate, endDate, urgency = '') => execute_generator(
    'GET', { service: api_url, resource: 'bookings/support_tickets/search?tenant=' + tenant + '&assigned=' + assigned + '&category=' + category + '&priority=' + priority + '&subject=' + subject + '&ticket_status=' + ticket_status + '&startDate=' + startDate + '&endDate=' + endDate + (urgency ? '&urgency=' + urgency : '')}
).next()

export const get_support_ticket = (tenant, id) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/support_ticket?tenant=' + tenant + '&id=' + id}
).next()

export const get_support_ticket_assist_export = (tenant, id) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/support_ticket/assist_export?tenant=' + tenant + '&id=' + id}
).next()

export const get_support_ticket_billable_summary = (month, assigned_to = '') => execute_generator(
    'GET', { service: api_url, resource: 'bookings/support_tickets/billable_summary?month=' + month
        + (assigned_to ? '&assigned_to=' + encodeURIComponent(assigned_to) : '')}
).next()

export const get_route_addresses = (tenant, route_group, route_filter) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/route/addresses?tenant=' + tenant + '&routeGroup=' + route_group + '&routeFilter=' + route_filter}
).next()

export const get_route_assignments = (tenant, route_filter, targetDate) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/route/assignments?tenant=' + tenant + '&routeFilter=' + route_filter + '&targetDate=' + targetDate}
).next()

export const get_groupsender_user_list = (tenant, user_type, driverStartTime, driverEndTime) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/groupsender/users?tenant=' + tenant + '&userType=' + user_type + '&driverStartTime=' + driverStartTime + '&driverEndTime=' + driverEndTime}
).next()

export const send_message_to_group = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/groupsender/send?tenant=' + tenant}, body
).next()

export const send_message_to_user = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/groupsender/senduser?tenant=' + tenant}, body
).next()

export const get_process_manager_list = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/processmgr/list?tenant=' + tenant}
).next()

export const update_process_enabled = (tenant, processId, enabled) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/processmgr/' + processId + '?tenant=' + tenant + '&enabled=' + enabled}
).next()

export const disable_all_processes = () => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/processmgr/disable_all'}
).next()

export const enable_all_processes = () => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/processmgr/enable_all'}
).next()

export const delete_process_record = (tenant, processId) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/processmgr/' + processId + '?tenant=' + tenant}
).next()

export const get_user = (tenant, username) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/users/' + username + '?tenant=' + tenant}
).next()

export const get_driver = (tenant, id) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/drivers/' + id + '?tenant=' + tenant}
).next()

export const delete_user = (tenant, username) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/users/' + username + '?tenant=' + tenant}
).next()

export const create_user = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/users?tenant=' + tenant}, body
).next()

export const update_user = (tenant, body) => execute_generator(
    'PUT', { service: api_url, resource: 'bookings/users?tenant=' + tenant}, body
).next()

export const update_user_technical_contact = (tenant, username, value) => execute_generator(
    'PUT', { service: api_url, resource: 'bookings/users/' + username + '/technical_contact?tenant=' + tenant}, { technical_contact: value }
).next()

export const get_all_users = () => execute_generator(
    'GET', { service: api_url, resource: 'bookings/users/all'}
).next()

export const delete_user_account = (username) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/admin/users/' + username}
).next()

export const update_driver = (tenant, body) => execute_generator(
    'PUT', { service: api_url, resource: 'bookings/drivers?tenant=' + tenant}, body
).next()

export const create_support_ticket = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/support_ticket?tenant=' + tenant}, body
).next()

export const update_support_ticket = (tenant, body, send_email) => execute_generator(
    'PUT', { service: api_url, resource: 'bookings/support_ticket?tenant=' + tenant + '&send_email=' + send_email}, body
).next()

// ticket board drag-to-rank: one lane's ordering per call, as
// { items: [{ tenant_id, id, queue_rank }, ...] }. admin-only server-side.
// no tenant query param -- a lane on the all-customers board spans tenants,
// so each item carries its own key.
export const rank_support_tickets = (body) => execute_generator(
    'PUT', { service: api_url, resource: 'bookings/support_tickets/rank' }, body
).next()

export const poke_support_ticket = (tenant, id) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/support_ticket/poke?tenant=' + tenant + '&id=' + id }, {}
).next()

// a reviewed DRAFT ticket goes live: status -> waiting and the customer
// gets the "ticket opened" email with the description. admin-only.
export const send_support_ticket_draft = (tenant, id) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/support_ticket/send_draft?tenant=' + tenant + '&id=' + id }, {}
).next()

export const delete_support_ticket = (tenant, ticket_id) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/support_tickets/' + ticket_id + '?tenant=' + tenant}
).next()

export const create_support_ticket_comment = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/support_ticket/comment?tenant=' + tenant}, body
).next()

export const update_support_ticket_comment = (tenant, body) => execute_generator(
    'PUT', { service: api_url, resource: 'bookings/support_ticket/comment?tenant=' + tenant}, body
).next()

export const delete_support_ticket_comment = (tenant, body) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/support_ticket/comment?tenant=' + tenant}, body
).next()

export const delete_support_ticket_attachments = (tenant, body) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/support_ticket/attachments?tenant=' + tenant}, body
).next()

export const presign_support_ticket_attachment = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/support_ticket/attachments/presign?tenant=' + tenant}, body
).next()

export const finalize_support_ticket_attachment = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/support_ticket/attachments/finalize?tenant=' + tenant}, body
).next()

// FI-1815: ops desk / erm authenticated event surface. list/detail/comments/
// status/edit back the new fleet-int-web ops desk page. submission (public,
// unauth) is at bookings/ops-desk/submit/<slug> and not called from here.
// `config` is the optional request config execute() takes; the erm live
// refresh passes { noAuthRedirect: true } so a background pass that 403s
// rejects instead of bouncing the reader to login (FI-2173).
export const list_ops_desk_events = (tenant, params = {}, config) => {
    const qs = Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&')
    return execute_generator(
        'GET',
        { service: api_url, resource: 'bookings/ops-desk/events?tenant=' + tenant + (qs ? '&' + qs : '') },
        undefined,
        config,
    ).next()
}

// FI-1932: filtered csv export. same query params as the list; streamed as a
// blob so the jwt rides the normal Authorization header (no window.open of an
// unauthenticated url). returns the raw axios response so the caller can read
// the content-disposition filename.
export const export_ops_desk_events = (tenant, params = {}) => {
    const qs = Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&')
    return download_file(
        'GET',
        { service: api_url, resource: 'bookings/ops-desk/events/export?tenant=' + tenant + (qs ? '&' + qs : '') },
    )
}

// aggregate payload for the orm analytics tab -- one call serves every
// chart. params: days (30/90/365) or from/to (YYYY-MM-DD pair)
export const get_ops_desk_analytics = (tenant, params = {}) => {
    const qs = Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&')
    return execute_generator(
        'GET',
        { service: api_url, resource: 'bookings/ops-desk/analytics?tenant=' + tenant + (qs ? '&' + qs : '') },
    ).next()
}

// per-driver erm counts over a SCORECARD period (start_month/end_month), for
// the Driver Manager risk matrix's outcome axis. counts only -- the outcome
// score normalises per 100k miles and miles live in the scorecard payload, so
// the formula runs client-side in utils/risk_matrix.js. driver keys come back
// as the raw free-text strings stored on the events; matching them to the
// scorecard roster is the caller's job.
export const get_ops_desk_driver_outcomes = (tenant, start_month, end_month) => execute_generator(
    'GET', {
        service: api_url,
        resource: 'bookings/ops-desk/driver-outcomes?tenant=' + tenant
            + '&start_month=' + start_month + '&end_month=' + end_month,
    }
).next()

export const get_ops_desk_event = (tenant, eventId, config) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/events/' + eventId + '?tenant=' + tenant },
    undefined, config,
).next()

// FI-1815 authed "+ Add entry" creation. mirrors the public /submit/<slug>
// route shape but with a json body instead of multipart form, identity-
// stamped submitter, no honeypot, and no rate-limit.
export const create_ops_desk_event = (tenant, payload) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/ops-desk/events?tenant=' + tenant }, payload
).next()

// FI-2201 (#1815) Add entry's duplicate check: issues filed in the last 7
// days with the same charter and category. params: entry_type, category,
// charter_ref; returns { enabled, window_days, matches: [{id, entry_type,
// category, status, created_at}] }. read-only and warn-only. it runs as the
// user types, so the modal passes { noAuthRedirect: true } in `config`: a
// 403 must not log them out mid-draft
export const get_ops_desk_possible_duplicates = (tenant, params = {}, config) => {
    const qs = Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&')
    return execute_generator(
        'GET',
        { service: api_url, resource: 'bookings/ops-desk/events/possible-duplicates?tenant=' + tenant + (qs ? '&' + qs : '') },
        undefined,
        config,
    ).next()
}

// schema for the in-app "+ Add entry" modal. mirrors the public form
// /form/<slug> response so the frontend can reuse the same renderer.
export const get_ops_desk_form_schema = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/form-schema?tenant=' + tenant }
).next()

export const add_ops_desk_event_comment = (tenant, eventId, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/ops-desk/events/' + eventId + '/comments?tenant=' + tenant }, { body }
).next()

export const transition_ops_desk_event_status = (tenant, eventId, status) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/ops-desk/events/' + eventId + '/status?tenant=' + tenant }, { status }
).next()

export const update_ops_desk_event = (tenant, eventId, payload) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/ops-desk/events/' + eventId + '?tenant=' + tenant }, payload
).next()

// FI-1812 photo staging (authed). presign returns {key, upload} and the
// browser POSTs the file straight to s3 (od_photo_input.vue), dodging
// the api-gateway payload cap; the staged keys then ride on the create
// payload (attachment_keys) or the per-event attach call below.
export const presign_ops_desk_attachment = (tenant, meta) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/ops-desk/attachments/presign?tenant=' + tenant }, meta
).next()

export const add_ops_desk_event_attachments = (tenant, eventId, keys) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/ops-desk/events/' + eventId + '/attachments?tenant=' + tenant }, { keys }
).next()

export const delete_ops_desk_event_attachment = (tenant, eventId, attachmentId) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/ops-desk/events/' + eventId + '/attachments/' + attachmentId + '?tenant=' + tenant }
).next()

// FI-2187 hard delete of a whole event (admin + customer-admin only, the
// tenant_erm_admin_required tier). the server drops the row with its
// comments, activity, photos and notification cards; the id is never reused.
export const delete_ops_desk_event = (tenant, eventId) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/ops-desk/events/' + eventId + '?tenant=' + tenant }
).next()

// FI-1816: sunday huddle. per-user opt-in subscription + tenant-level
// schedule + admin preview/send-now. schedule editor ui lands with FI-1818
// (tenant admin); this file already speaks PATCH for it so 1818 just calls
// update_ops_desk_huddle_schedule when the form posts.
export const get_ops_desk_huddle_subscription = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/huddle/subscription?tenant=' + tenant }
).next()

export const subscribe_ops_desk_huddle = (tenant) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/ops-desk/huddle/subscription?tenant=' + tenant }, {}
).next()

export const unsubscribe_ops_desk_huddle = (tenant) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/ops-desk/huddle/subscription?tenant=' + tenant }
).next()

export const get_ops_desk_huddle_schedule = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/huddle/schedule?tenant=' + tenant }
).next()

export const update_ops_desk_huddle_schedule = (tenant, payload) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/ops-desk/huddle/schedule?tenant=' + tenant }, payload
).next()

export const send_ops_desk_huddle_now = (tenant, dryRun = false) => execute_generator(
    'POST', {
        service: api_url,
        resource: 'bookings/ops-desk/huddle/send-now?tenant=' + tenant + (dryRun ? '&dry_run=1' : '')
    }, {}
).next()

// FI-1813 public form. unauth endpoint; backend ignores the bogus
// "Bearer null" header execute_generator sends when no jwt is stored.
// schema GET hydrates the form; submit posts multipart form-data so the
// existing submission_service (request.form) doesn't need to learn JSON.
export const get_ops_desk_public_form_schema = (slug) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/form/' + encodeURIComponent(slug) }
).next()

export const submit_ops_desk_public_form = (slug, formData) => execute_generator(
    'POST',
    { service: api_url, resource: 'bookings/ops-desk/submit/' + encodeURIComponent(slug) },
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
).next()

// FI-1812 public photo staging: unauth mirror of the presign route,
// slug-scoped and rate-limited server-side.
export const presign_ops_desk_public_attachment = (slug, meta) => execute_generator(
    'POST',
    { service: api_url, resource: 'bookings/ops-desk/form/' + encodeURIComponent(slug) + '/attachments' },
    meta
).next()


// FI-1818 tenant admin. composite GET hydrates the whole settings page in
// one round-trip; granular PATCHes let each tab save independently and
// surface its own errors (especially the slug uniqueness/shape errors,
// which come back as 400 with a human-readable message).
export const get_ops_desk_settings = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/settings?tenant=' + tenant }
).next()

export const update_ops_desk_slug = (tenant, slug) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/ops-desk/settings/slug?tenant=' + tenant }, { slug }
).next()

// FI-1818 public form access toggle. when disabled, the unauth /op/<slug>
// route renders a "form unavailable" state and the public submit endpoint
// rejects, but staff in-app Add Entry remains available.
export const update_ops_desk_access = (tenant, public_form_enabled) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/ops-desk/settings/access?tenant=' + tenant }, { public_form_enabled }
).next()

// FI-2087 per-tenant toggle for the submitter status-change emails. only
// the email leg; in-app status cards keep flowing either way.
// FI-2116 canonical driver roster for the link-driver typeahead. authed
// surfaces only -- the public form never receives this list.
export const get_ops_desk_driver_roster = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/driver-roster?tenant=' + tenant }
).next()

// FI-2098 canonical vehicle names (vehicle-info), same authed-only contract
export const get_ops_desk_vehicle_roster = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/vehicle-roster?tenant=' + tenant }
).next()

// partial: send only the keys being changed (status_change_emails_enabled,
// email_subject_format). FI-2087 + FI-2097 share the route.
export const update_ops_desk_notification_settings = (tenant, payload) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/ops-desk/settings/notifications?tenant=' + tenant }, payload
).next()

// FI-2201 (#1815) the General tab's own settings row keys
// (duplicate_check_enabled). partial: send only the keys being changed.
// the tab's two email rules keep update_ops_desk_notification_settings
export const update_ops_desk_general_settings = (tenant, payload) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/ops-desk/settings/general?tenant=' + tenant }, payload
).next()

// non-mutating availability + preview short URL for the slug input.
// returns { slug, available, reason, preview_short_url }. used by the
// settings tab to give the admin live feedback as they type.
export const check_ops_desk_slug = (tenant, slug) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/settings/slug-check?tenant=' + tenant + '&slug=' + encodeURIComponent(slug || '') }
).next()

export const update_ops_desk_distribution = (tenant, payload) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/ops-desk/settings/distribution?tenant=' + tenant }, payload
).next()

export const get_huddle_subscribers = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/huddle/subscribers?tenant=' + tenant }
).next()

export const update_huddle_subscribers = (tenant, payload) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/ops-desk/huddle/subscribers?tenant=' + tenant }, payload
).next()

export const update_ops_desk_categories = (tenant, payload) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/ops-desk/settings/categories?tenant=' + tenant }, payload
).next()

// FI-1944 (#1816) a fleet's own categories. add takes {label, entry_type}
// (incident or kudos) and turns the new category on; remove is refused while
// any event uses the category. both answer with the categories payload (the
// row plus the available_* lists); a refusal is a 400 carrying the message.
export const add_ops_desk_custom_category = (tenant, payload) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/ops-desk/settings/categories/custom?tenant=' + tenant }, payload
).next()

export const remove_ops_desk_custom_category = (tenant, key) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/ops-desk/settings/categories/custom/' + encodeURIComponent(key) + '?tenant=' + tenant }
).next()

export const update_ops_desk_departments = (tenant, departments) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/ops-desk/settings/departments?tenant=' + tenant }, { departments }
).next()

// FI-1964 self-service Google Sheet -> ops events import. config GET is user-visible;
// the PATCH/test/preview/run mutations are admin-gated server-side. preview is
// a dry-run reconcile (no writes); run applies (unless dry_run).
export const get_ops_desk_import_config = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/import/config?tenant=' + tenant }
).next()

export const update_ops_desk_import_config = (tenant, payload) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/ops-desk/import/config?tenant=' + tenant }, payload
).next()

// connect-wizard step: resolves a pasted sheet link into the document title +
// tab list so the admin picks a tab instead of typing its name.
export const inspect_ops_desk_import = (tenant, payload) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/ops-desk/import/inspect?tenant=' + tenant }, payload || {}
).next()

export const test_ops_desk_import = (tenant, payload) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/ops-desk/import/test?tenant=' + tenant }, payload || {}
).next()

export const preview_ops_desk_import = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/import/preview?tenant=' + tenant }
).next()

export const run_ops_desk_import = (tenant, dryRun = false) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/ops-desk/import/run?tenant=' + tenant + (dryRun ? '&dry_run=1' : '') }, {}
).next()

// preview returns html (when format=html) or json. fetched via the generator
// so the auth header is attached; component embeds the html in an iframe.
export const get_ops_desk_huddle_preview_html = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/huddle/preview?tenant=' + tenant + '&format=html' }
).next()

export const get_ops_desk_huddle_preview_json = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/huddle/preview?tenant=' + tenant }
).next()

// ops board: per-yard today view. the board GET aggregates departing /
// out-now / returning trips plus needs-attention verdicts in one payload;
// yard is optional (null/undefined = all yards).
export const get_ops_desk_board = (tenant, yard) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/board?tenant=' + tenant + (yard ? '&yard=' + encodeURIComponent(yard) : '') }
).next()

// toggles a verdict's acknowledged flag for the given board date.
// payload: { date, verdict_key, acknowledged }
export const ack_ops_desk_board = (tenant, payload) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/ops-desk/board/ack?tenant=' + tenant }, payload
).next()

// fleet assistant brief for the today board. cached server-side on a
// board-data fingerprint; force=1 (managing roles only) regenerates.
export const get_ops_desk_board_brief = (tenant, force) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/board/brief?tenant=' + tenant + (force ? '&force=1' : '') }
).next()

// Coaching Portal (Driver Manager) POC. tickets are the accountability queue
// fed by scorecard flags (erm / accident sources reserved). list returns
// { tickets, summary } — summary counts cover the whole queue regardless of
// filters so the tiles stay stable while the table narrows.
// FI-2248 csv of the rows the Coaching Tickets page is showing (its filters
// are client-side, so it sends the ids). blob download like the erm export.
export const export_coaching_tickets = (tenant, ticketIds) => download_file(
    'POST',
    { service: api_url, resource: 'bookings/ops-desk/coaching/tickets/export?tenant=' + tenant },
    { data: { ticket_ids: ticketIds } },
)

export const get_coaching_tickets = (tenant, params = {}) => {
    const qs = Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&')
    return execute_generator(
        'GET',
        { service: api_url, resource: 'bookings/ops-desk/coaching/tickets?tenant=' + tenant + (qs ? '&' + qs : '') },
    ).next()
}

export const create_coaching_ticket = (tenant, payload) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/ops-desk/coaching/tickets?tenant=' + tenant }, payload
).next()

// status may only move between open and scheduled here (plus assigned_to /
// due_date / scheduled_for) — completing a ticket goes through sign_off,
// the only path that captures the signature a close requires.
export const update_coaching_ticket = (tenant, ticketId, payload) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/ops-desk/coaching/tickets/' + ticketId + '?tenant=' + tenant }, payload
).next()

// reopening a signed-off ticket. its own verb, not a PATCH: this is the one
// call that touches a closed record, and it writes the undo to the trail.
export const reopen_coaching_ticket = (tenant, ticketId, payload) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/ops-desk/coaching/tickets/' + ticketId + '/reopen?tenant=' + tenant }, payload
).next()

// permanent delete for a ticket raised in error. fleet admins only
// (server-gated); signed-off tickets are refused — reopen first.
export const delete_coaching_ticket = (tenant, ticketId) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/ops-desk/coaching/tickets/' + ticketId + '?tenant=' + tenant }
).next()

// attachments: presign -> upload straight to s3 -> finalize onto the ticket.
// read urls are minted per click (never embedded in the ticket payload).
export const presign_coaching_attachment = (tenant, payload) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/ops-desk/coaching/attachments/presign?tenant=' + tenant }, payload
).next()

export const add_coaching_attachments = (tenant, ticketId, keys) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/ops-desk/coaching/tickets/' + ticketId + '/attachments?tenant=' + tenant }, { keys }
).next()

export const delete_coaching_attachment = (tenant, ticketId, attachmentId) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/ops-desk/coaching/tickets/' + ticketId + '/attachments/' + attachmentId + '?tenant=' + tenant }
).next()

export const get_coaching_attachment_url = (tenant, ticketId, attachmentId) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/coaching/tickets/' + ticketId + '/attachments/' + attachmentId + '/url?tenant=' + tenant }
).next()

export const sign_off_coaching_ticket = (tenant, ticketId, payload) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/ops-desk/coaching/tickets/' + ticketId + '/signoff?tenant=' + tenant }, payload
).next()

export const add_coaching_ticket_note = (tenant, ticketId, payload) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/ops-desk/coaching/tickets/' + ticketId + '/notes?tenant=' + tenant }, payload
).next()

// R4: push a coaching (incident) or recognition (kudos) ticket from a
// driver-tagged erm event. gated server-side on coaching_portal_enabled.
export const create_coaching_ticket_from_event = (tenant, eventId, payload) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/ops-desk/events/' + eventId + '/coaching?tenant=' + tenant }, payload || {}
).next()

// R2: the effectiveness triad (coverage, timeliness, recidivism) + per-coach stats
export const get_coaching_effectiveness = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/coaching/effectiveness?tenant=' + tenant }
).next()

// R3: per-tenant SLA settings (due days, escalation threshold, recidivism window).
// GET is open to anyone who can work the queue; PATCH is fleet-admin only.
export const get_coaching_settings = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ops-desk/coaching/settings?tenant=' + tenant }
).next()

export const update_coaching_settings = (tenant, payload) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/ops-desk/coaching/settings?tenant=' + tenant }, payload
).next()


export const send_chat_message = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/chat?tenant=' + tenant}, body
).next()

export const get_chat_session = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/chat/session?tenant=' + tenant}
).next()

export const get_ai_usage = (tenant, period) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ai-usage?tenant=' + tenant + (period ? '&period=' + period : '')}
).next()

export const reset_ai_usage = (tenant) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/ai-usage?tenant=' + tenant}
).next()

export const get_ai_usage_history = (tenant, months = 12) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ai-usage/history?tenant=' + tenant + '&months=' + months}
).next()

export const get_ai_usage_users = (tenant, period) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/ai-usage/users?tenant=' + tenant + (period ? '&period=' + period : '')}
).next()

export const get_ai_usage_fleet = (tenants, period) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/ai-usage/fleet'}, { tenants, period: period || null }
).next()

export const submit_chat_feedback = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/chat/feedback?tenant=' + tenant}, body
).next()

export const list_chat_feedback = (params) => {
    const qs = Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => k + '=' + encodeURIComponent(v)).join('&')
    return execute_generator('GET', { service: api_url, resource: 'bookings/chat/feedback?' + qs }).next()
}

export const update_chat_feedback_status = (feedbackId, body) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/chat/feedback/' + encodeURIComponent(feedbackId)}, body
).next()

export const delete_chat_feedback = (tenant, feedbackId) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/chat/feedback/' + encodeURIComponent(feedbackId) + '?tenant=' + encodeURIComponent(tenant)}
).next()

export const restore_chat_feedback = (tenant, feedbackId) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/chat/feedback/' + encodeURIComponent(feedbackId) + '/restore?tenant=' + encodeURIComponent(tenant)}
).next()

export const get_chat_feedback_stats = (params) => {
    const qs = Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => k + '=' + encodeURIComponent(v)).join('&')
    return execute_generator('GET', { service: api_url, resource: 'bookings/chat/feedback/stats?' + qs }).next()
}

export const get_chat_feedback_item = (tenant, feedbackId) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/chat/feedback/item?tenant=' + tenant + '&feedback_id=' + encodeURIComponent(feedbackId)}
).next()

export const get_help_feedback_leaderboard = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/help/feedback/leaderboard?tenant=' + tenant}
).next()

export const get_admin_chat_session = (tenant, sessionId) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/chat/session/admin?tenant=' + tenant + '&session_id=' + encodeURIComponent(sessionId)}
).next()

// Assistant Insights (admin-only): live session lookup by user_id (server-side md5)
export const get_admin_chat_live_session = (tenant, userId) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/admin/chat/live-session?tenant=' + tenant + '&user_id=' + encodeURIComponent(userId)}
).next()

// Assistant Insights (admin-only): archived sessions + tool summary
export const list_admin_chat_archived_sessions = (tenant, params = {}) => {
    const qs = Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => k + '=' + encodeURIComponent(v)).join('&')
    return execute_generator('GET', { service: api_url, resource: 'bookings/admin/chat/archived-sessions?tenant=' + tenant + (qs ? '&' + qs : '') }).next()
}

export const get_admin_chat_archived_session = (tenant, sessionId) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/admin/chat/archived-session?tenant=' + tenant + '&session_id=' + encodeURIComponent(sessionId)}
).next()

export const get_admin_chat_tools_summary = (tenant, period) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/admin/chat/tools-summary?tenant=' + tenant + (period ? '&period=' + period : '')}
).next()

// Assistant Insights (admin-only): cross-tenant archived/live sessions for one user.
// used by the support-tenant user view, where chats are billed to support but
// session rows live under each customer tenant the admin chatted in.
export const list_admin_chat_archived_sessions_cross_tenant = (tenants, userId, limit = 50) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/admin/chat/archived-sessions-cross-tenant'}, { tenants, user_id: userId, limit }
).next()

export const get_admin_chat_live_sessions_cross_tenant = (tenants, userId) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/admin/chat/live-sessions-cross-tenant'}, { tenants, user_id: userId }
).next()

export const clear_chat_session = (tenant) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/chat/session?tenant=' + tenant}
).next()

export const get_chat_instructions = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/chat/instructions?tenant=' + tenant}
).next()

// Fleet Assistant documents (company-level: admin only)
export const list_fa_documents_company = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/fa-documents/company?tenant=' + tenant}
).next()

export const presign_fa_document_company = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/fa-documents/company/presign?tenant=' + tenant }, body
).next()

export const finalize_fa_document_company = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/fa-documents/company/finalize?tenant=' + tenant }, body
).next()

export const upload_fa_document_company = (tenant, formData) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/fa-documents/company?tenant=' + tenant }, formData
).next()

export const delete_fa_document_company = (tenant, docId) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/fa-documents/company/' + encodeURIComponent(docId) + '?tenant=' + tenant}
).next()

// Fleet Assistant documents (user-level)
export const list_fa_documents_user = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/fa-documents/user?tenant=' + tenant}
).next()

export const presign_fa_document_user = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/fa-documents/user/presign?tenant=' + tenant }, body
).next()

export const finalize_fa_document_user = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/fa-documents/user/finalize?tenant=' + tenant }, body
).next()

export const upload_fa_document_user = (tenant, formData) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/fa-documents/user?tenant=' + tenant }, formData
).next()

export const delete_fa_document_user = (tenant, docId) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/fa-documents/user/' + encodeURIComponent(docId) + '?tenant=' + tenant}
).next()

export const bulk_delete_fa_documents_user = (tenant, docIds) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/fa-documents/user/bulk-delete?tenant=' + tenant }, { doc_ids: docIds }
).next()

// Fleet Assistant documents (admin: storage summary & user purge)
export const get_fa_documents_summary = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/fa-documents/summary?tenant=' + tenant}
).next()

export const purge_fa_documents_user = (tenant, userId) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/fa-documents/user/' + encodeURIComponent(userId) + '/purge?tenant=' + tenant}
).next()

export const list_help_articles = (tenant, params = {}) => {
    const route_name = params.route_name ? '&route_name=' + encodeURIComponent(params.route_name) : ''
    const query = params.query ? '&query=' + encodeURIComponent(params.query) : ''
    const enabled_features = Array.isArray(params.enabled_features) && params.enabled_features.length > 0
        ? '&enabled_features=' + encodeURIComponent(params.enabled_features.join(','))
        : ''
    const limit = params.limit ? '&limit=' + encodeURIComponent(params.limit) : ''
    return execute_generator(
        'GET', { service: api_url, resource: 'bookings/help/articles?tenant=' + tenant + route_name + query + enabled_features + limit }
    ).next()
}

export const get_help_article = (tenant, docId, params = {}) => {
    const offset = params.offset ? '&offset=' + encodeURIComponent(params.offset) : ''
    const limit = params.limit ? '&limit=' + encodeURIComponent(params.limit) : ''
    return execute_generator(
        'GET', { service: api_url, resource: 'bookings/help/articles/' + encodeURIComponent(docId) + '?tenant=' + tenant + offset + limit }
    ).next()
}

export const submit_help_feedback = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/help/feedback?tenant=' + tenant }, body
).next()

export const record_help_article_view = (tenant, docId) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/help/articles/' + encodeURIComponent(docId) + '/view?tenant=' + tenant }, {}
).next()

export const get_help_feedback = (tenant, docId) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/help/feedback/summary?tenant=' + tenant + '&doc_id=' + encodeURIComponent(docId) }
).next()

export const list_help_company_documents = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/help/company-documents?tenant=' + tenant }
).next()

export const get_help_company_document = (tenant, docId) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/help/company-documents/' + encodeURIComponent(docId) + '?tenant=' + tenant }
).next()

export const download_help_company_document = (tenant, docId) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/help/company-documents/' + encodeURIComponent(docId) + '/download?tenant=' + tenant }
).next()

export const get_url_shortener_stats = (tenant_id) => {
    const qs = tenant_id ? '?tenant_id=' + encodeURIComponent(tenant_id) : ''
    return execute_generator(
        'GET', { service: api_url, resource: 'bookings/url-shortener/stats' + qs }
    ).next()
}

export const get_url_shortener_urls = (tenant_id, start_date, end_date, limit, last_key) => {
    let qs = '?limit=' + (limit || 50)
    if (tenant_id) qs += '&tenant_id=' + encodeURIComponent(tenant_id)
    if (start_date) qs += '&start_date=' + encodeURIComponent(start_date)
    if (end_date) qs += '&end_date=' + encodeURIComponent(end_date)
    if (last_key) qs += '&last_key=' + encodeURIComponent(last_key)
    return execute_generator(
        'GET', { service: api_url, resource: 'bookings/url-shortener/urls' + qs }
    ).next()
}

export const create_url_shortener_url = (long_url, tenant_id) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/url-shortener/urls' }, { long_url, tenant_id }
).next()

export const shorten_url_for_vsm = (long_url, tenant) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/url-shortener/shorten' }, { long_url, tenant }
).next()

export const delete_url_shortener_url = (short_code) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/url-shortener/urls/' + encodeURIComponent(short_code) }
).next()

// Map Link Builder (Maps > Map Link Builder): shareable live-tracking links
export const get_map_links = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/maps/links?tenant=' + tenant }
).next()

export const get_map_link_shortcuts = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/maps/links/shortcuts?tenant=' + tenant }
).next()

export const create_map_link = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/maps/links?tenant=' + tenant }, body
).next()

export const update_map_link = (tenant, token, body) => execute_generator(
    'PATCH', { service: api_url, resource: 'bookings/maps/links/' + encodeURIComponent(token) + '?tenant=' + tenant }, body
).next()

export const delete_map_link = (tenant, token) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/maps/links/' + encodeURIComponent(token) + '?tenant=' + tenant }
).next()

// public viewer endpoint - no auth, resolved/enforced server-side by token
export const get_map_link_track = (token) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/maps/links/' + encodeURIComponent(token) + '/track' }
).next()

export const get_url_shortener_clicks = (short_code, limit, last_key) => {
    let qs = '?limit=' + (limit || 50)
    if (last_key) qs += '&last_key=' + encodeURIComponent(last_key)
    return execute_generator(
        'GET', { service: api_url, resource: 'bookings/url-shortener/urls/' + encodeURIComponent(short_code) + '/clicks' + qs }
    ).next()
}

export const get_api_usage = (tenant, start_date, end_date, tenant_ids) => {
    let qs = '?start_date=' + start_date + '&end_date=' + end_date
    if (tenant) qs += '&tenant=' + tenant
    else if (tenant_ids && tenant_ids.length) qs += '&tenant_ids=' + tenant_ids.join(',')
    return execute_generator(
        'GET', { service: api_url, resource: 'bookings/admin/api-usage' + qs }
    ).next()
}

export const get_telematics_api_usage = (tenant, start_date, end_date, tenant_ids) => {
    let qs = '?start_date=' + start_date + '&end_date=' + end_date
    if (tenant) qs += '&tenant=' + tenant
    else if (tenant_ids && tenant_ids.length) qs += '&tenant_ids=' + tenant_ids.join(',')
    return execute_generator(
        'GET', { service: api_url, resource: 'bookings/admin/telematics-api-usage' + qs }
    ).next()
}

export const get_sms_usage = (tenant, start_date, end_date, tenant_ids) => {
    let qs = '?start_date=' + start_date + '&end_date=' + end_date
    if (tenant) qs += '&tenant=' + tenant
    else if (tenant_ids && tenant_ids.length) qs += '&tenant_ids=' + tenant_ids.join(',')
    return execute_generator(
        'GET', { service: api_url, resource: 'bookings/admin/sms-usage' + qs }
    ).next()
}

export const get_sms_usage_messages = (start_date, end_date, opts) => {
    opts = opts || {}
    let qs = '?start_date=' + start_date + '&end_date=' + end_date
    if (opts.tenant) qs += '&tenant=' + opts.tenant
    if (opts.status) qs += '&status=' + opts.status
    if (opts.direction) qs += '&direction=' + opts.direction
    if (opts.context) qs += '&context=' + encodeURIComponent(opts.context)
    if (opts.limit) qs += '&limit=' + opts.limit
    if (opts.cursor) qs += '&cursor=' + encodeURIComponent(JSON.stringify(opts.cursor))
    return execute_generator(
        'GET', { service: api_url, resource: 'bookings/admin/sms-usage/messages' + qs }
    ).next()
}

export const get_google_maps_api_usage = (tenant, start_date, end_date, tenant_ids) => {
    let qs = '?start_date=' + start_date + '&end_date=' + end_date
    if (tenant) qs += '&tenant=' + tenant
    else if (tenant_ids && tenant_ids.length) qs += '&tenant_ids=' + tenant_ids.join(',')
    return execute_generator(
        'GET', { service: api_url, resource: 'bookings/admin/google-maps-usage' + qs }
    ).next()
}

export const admin_clear_cache = (tenant, cache_type) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/admin/cache/clear?tenant=' + tenant + '&cache_type=' + cache_type }
).next()


// notification center (FI-1432)
// the @tenant_user_required decorator requires a tenant query param to
// validate access; the endpoint itself is user-scoped but the tenant
// param is still required to pass the decorator check.

// `config` is the optional request config execute() takes; App.vue's
// background poll passes { noAuthRedirect: true } so a 403 on a quiet tick
// rejects instead of bouncing the reader to login (FI-2180).
export const get_user_notifications = (tenant, limit = 20, cursor, config) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/notifications?tenant=' + tenant + '&limit=' + limit + (cursor ? '&cursor=' + encodeURIComponent(JSON.stringify(cursor)) : '') },
    undefined, config
).next()

export const get_notifications_unread_count = (tenant, config) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/notifications/unread-count?tenant=' + tenant }, undefined, config
).next()

export const mark_notifications_read = (tenant, notification_ids) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/notifications/read?tenant=' + tenant }, { tenant, notification_ids }
).next()

export const mark_all_notifications_read = (tenant) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/notifications/read?tenant=' + tenant }, { tenant, all: true }
).next()

export const record_notification_action = (tenant, notification_id) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/notifications/action?tenant=' + tenant }, { tenant, notification_id }
).next()

export const get_user_announcement = (tenant, announcement_id) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/notifications/announcements/' + encodeURIComponent(announcement_id) + '?tenant=' + tenant }
).next()

export const register_email_when_ready = (body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/notifications/email-when-ready?tenant=' + (body && body.tenant) }, body
).next()

export const register_notification_device = (body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/notifications/device?tenant=' + (body && body.tenant) }, body
).next()

export const unregister_notification_device = (tenant, device_id) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/notifications/device/' + encodeURIComponent(device_id) + '?tenant=' + tenant }
).next()

export const admin_create_announcement = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/admin/announcements?tenant=' + tenant }, body
).next()

export const admin_list_announcements = (tenant, limit = 20, cursor) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/admin/announcements?tenant=' + tenant + '&limit=' + limit + (cursor ? '&cursor=' + encodeURIComponent(JSON.stringify(cursor)) : '') }
).next()

export const admin_get_announcement_stats = (tenant, announcement_id) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/admin/announcements/' + encodeURIComponent(announcement_id) + '/stats?tenant=' + tenant }
).next()

// read-only audience sizing for the composer. sends nothing; reuses the same
// role-exclusion and preference helpers as the send path so the count shown
// before publishing is the count that goes out.
export const admin_preview_announcement_audience = (tenant, { target, tenant_id, roles } = {}) => execute_generator(
    'GET', {
        service: api_url,
        resource: 'bookings/admin/announcements/audience?tenant=' + tenant
            + '&target=' + encodeURIComponent(target || 'global')
            + (tenant_id ? '&tenant_id=' + encodeURIComponent(tenant_id) : '')
            + (roles && roles.length ? '&roles=' + encodeURIComponent(roles.join(',')) : ''),
    }
).next()

export const admin_delete_announcement = (tenant, announcement_id) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/admin/announcements/' + encodeURIComponent(announcement_id) + '?tenant=' + tenant }
).next()

// retract a published announcement — drops per-user notifications and
// flips the canonical row back to status='draft' so the admin can edit
// and republish. draft rows use admin_delete_announcement instead.
export const admin_retract_announcement = (tenant, announcement_id) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/admin/announcements/' + encodeURIComponent(announcement_id) + '/retract?tenant=' + tenant }
).next()

// full announcement row (including body_markdown) for the detail page.
export const admin_get_announcement = (tenant, announcement_id) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/admin/announcements/' + encodeURIComponent(announcement_id) + '?tenant=' + tenant }
).next()

// patch a draft's fields. returns 409 if the row is already published.
export const admin_update_announcement = (tenant, announcement_id, body) => execute_generator(
    'PUT', { service: api_url, resource: 'bookings/admin/announcements/' + encodeURIComponent(announcement_id) + '?tenant=' + tenant }, body
).next()

// promote a draft to published and fan out to recipients.
export const admin_publish_announcement = (tenant, announcement_id) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/admin/announcements/' + encodeURIComponent(announcement_id) + '/publish?tenant=' + tenant }, {}
).next()

// expire (or schedule expiry of) the persistent site-wide banner on a
// published service_alert. body.when=null/missing → expire now (the
// "outage over" path); body.when='<iso>' → schedule a future expiry;
// body.when='' → clear an existing expiry. notification rows stay in
// the user's dropdown either way — only the banner bar is hidden.
export const admin_expire_announcement_banner = (tenant, announcement_id, when) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/admin/announcements/' + encodeURIComponent(announcement_id) + '/expire-banner?tenant=' + tenant }, { when: when == null ? null : when }
).next()

export const admin_get_notifications_system_stats = (tenant, days = 7) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/admin/notifications/system-stats?tenant=' + tenant + '&days=' + days }
).next()

export const presign_announcement_image = (tenant, body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/admin/announcements/images/presign?tenant=' + tenant }, body
).next()

export const seed_demo_notifications = (tenant) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/admin/notifications/seed-demo?tenant=' + tenant }, {}
).next()

// absolute image URL that markdown embeds reference — resolves to a
// short-lived presigned S3 redirect so embedded screenshots keep working
// for all authenticated recipients across CM + TBN tenants.
export const announcement_image_url = (image_id) =>
    api_url + 'bookings/announcements/images/' + encodeURIComponent(image_id)

export const get_driver_contacts = (token) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/drivers/contacts/' + token }
).next()

export const cm_get_drivers_scorecard_list = (tenant) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/drivers/scorecard-list?tenant=' + tenant }
).next()

export const cm_clear_scorecard_driver_list_cache = (tenant) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/drivers/scorecard-list-cache-clear?tenant=' + tenant }
).next()

export const cm_get_driver_scorecard = (tenant, driver_id, start_month, end_month) => execute_generator(
    'GET', {
        service: api_url,
        resource: 'bookings/driver-scorecard?tenant=' + tenant
            + '&driver_id=' + driver_id
            + '&start_month=' + start_month
            + '&end_month=' + end_month
            + '&include_comparison=true'
    }
).next()

export const cm_get_fleet_scorecard = (tenant, start_month, end_month) => execute_generator(
    'GET', {
        service: api_url,
        resource: 'bookings/fleet-scorecard?tenant=' + tenant
            + '&start_month=' + start_month
            + '&end_month=' + end_month
    }
).next()

export const cm_get_all_driver_scorecards = (tenant, start_month, end_month) => execute_generator(
    'GET', {
        service: api_url,
        resource: 'bookings/driver-scorecard/all?tenant=' + tenant
            + '&start_month=' + start_month
            + '&end_month=' + end_month
    }
).next()

export const cm_get_driver_scorecard_trends = (tenant, start_month, end_month, driver_id) => {
    let resource = 'bookings/driver-scorecard/trends?tenant=' + tenant + '&start_month=' + start_month + '&end_month=' + end_month
    if (driver_id) resource += '&driver_id=' + encodeURIComponent(driver_id)
    return execute_generator('GET', { service: api_url, resource }).next()
}

export const cm_scorecard_all_csv_url = (tenant, start_month, end_month) =>
    api_url + 'bookings/driver-scorecard/all/csv?tenant=' + tenant
        + '&start_month=' + start_month
        + '&end_month=' + end_month

export const cm_scorecard_comparison_csv_url = (tenant, driver_ids, driver_names, start_month, end_month) =>
    api_url + 'bookings/driver-scorecard/comparison/csv?tenant=' + tenant
        + '&driver_ids=' + driver_ids.join(',')
        + '&driver_names=' + driver_names.join('|')
        + '&start_month=' + start_month
        + '&end_month=' + end_month

// FI-2182 passkeys. management calls carry noAuthRedirect: a 403/43 for a
// role outside the api allow-list, or a 404 on a stage with the flag off, is
// the passkeys section's to explain, never a reason to touch the session.
export const get_passkeys = () => execute_generator(
    'GET', { service: api_url, resource: 'bookings/auth/passkeys' }, undefined, { noAuthRedirect: true }
).next()

// body: {} to add, { replace_credential_id } to swap one of your own
export const passkey_register_begin = (body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/auth/passkeys/register/begin' }, body || {}, { noAuthRedirect: true }
).next()

// body: { challenge_token, credential, nickname }
export const passkey_register_complete = (body) => execute_generator(
    'POST', { service: api_url, resource: 'bookings/auth/passkeys/register/complete' }, body, { noAuthRedirect: true }
).next()

export const delete_passkey = (credential_id) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/auth/passkeys/' + encodeURIComponent(credential_id) }, undefined, { noAuthRedirect: true }
).next()

// admin recovery on another account (lost device)
export const get_user_passkeys = (username) => execute_generator(
    'GET', { service: api_url, resource: 'bookings/users/' + encodeURIComponent(username) + '/passkeys' }, undefined, { noAuthRedirect: true }
).next()

export const delete_user_passkey = (username, credential_id) => execute_generator(
    'DELETE', { service: api_url, resource: 'bookings/users/' + encodeURIComponent(username) + '/passkeys/' + encodeURIComponent(credential_id) }, undefined, { noAuthRedirect: true }
).next()

// the sign-in ceremony: public routes with no bearer, posted the way
// login.vue posts the password. both resolve to the raw axios response.
export const passkey_login_begin = () => axios.post(api_url + 'bookings/auth/passkeys/login/begin', {})

export const passkey_login_complete = (body) => axios.post(api_url + 'bookings/auth/passkeys/login/complete', body)
